<template>
  <div class="operating-currencies-table">
    <!-- Title row per the component set (Figma node 1130:52): heading on the
         left, bulk actions top-right on the same row. -->
    <div class="operating-currencies-table__header">
      <div class="operating-currencies-table__heading">
        <h2 class="operating-currencies-table__title">
          Currencies used in this account
        </h2>
        <p class="operating-currencies-table__subtitle">
          This configuration enables features in the Fair Pay module
        </p>
      </div>
      <div class="operating-currencies-table__bulk-actions">
        <Button
          label="Set all to Corporate"
          :outline="'outline-active'"
          size="small"
          buttonWidth="auto"
          @onClick="handleBulkSetMode('corporate')"
        />
        <Button
          label="Set all to Auto"
          :outline="'outline-active'"
          size="small"
          buttonWidth="auto"
          @onClick="handleBulkSetMode('daily')"
        />
      </div>
    </div>
    <!-- FX banner sits outside UniversalTable so search/filter empty states
         do not move or clip the copy. -->
    <div class="operating-currencies-table__fx-banner">
      <span class="operating-currencies-table__fx-icon" aria-hidden="true">
        i
      </span>
      <p class="operating-currencies-table__fx-text">
        Exchange rates are updated daily.
        <strong>Auto</strong> currencies refresh each UTC day from
        <a
          class="operating-currencies-table__fx-link"
          href="https://www.exchangerate-api.com"
          target="_blank"
          rel="noopener noreferrer"
        >ExchangeRate-API</a>.
        <strong>Corporate</strong> currencies use a rate you set manually.
      </p>
    </div>
    <UniversalTable
      :config="tableConfigWithFilters"
      :items="paginatedCurrencies"
      @onClick="viewState.state.filters.showFilterDialog = true"
      @onSearch="handleSearch"
      @onSort="handleSort"
      @removeFilter="removeFilter"
      @removeAllFilters="removeAllFilters"
      @onActionClick="handleAddCurrency"
      backend-pagination
      @onRequest="handleRequest"
      :externalCurrentPage="currentPage"
      :items-count="filteredCount"
      :cellContainerMaxWidth="tableConfig.tableParams.value.cellContainerMaxWidth"
      :emptyObjectData="{
        singular: 'Operating Currency',
        plural: 'Currencies',
        emptyButtonLabel: 'Add Operating Currency',
      }"
    />
    <OperatingCurrenciesTableFilterModal
      :value="viewState.state.filters.showFilterDialog"
      :status-filter="viewState.state.filters.values"
      :rate-mode-filter="viewState.state.filters.rateModeValues"
      @input="viewState.state.filters.showFilterDialog = $event"
      @apply="applyFilters"
      @close="viewState.state.filters.showFilterDialog = false"
    />
    <AddOperatingCurrencyDialog
      :dialog="showAddDialog"
      :dropdownCurrencyList="dropdownCurrencyList"
      :operatingCatalogCurrencyIds="operatingCatalogCurrencyIds"
      :error-code="addErrorCode"
      @onClose="showAddDialog = false"
      @onAddCurrency="onAddCurrencyConfirm"
    />
    <ExActionsPopUp
      v-if="showRetirePopup"
      headline="Retire Currency"
      :htmlInfo="retireHtmlInfo"
      actionLabel="Retire"
      :value="showRetirePopup"
      :termination="true"
      :deletePopup="true"
      icon="currency_icon"
      @onAction="confirmRetire"
      @input="cancelRetire"
    />
    <ExActionsPopUp
      v-if="showReactivatePopup"
      headline="Re-Activate Currency"
      :htmlInfo="reactivateHtmlInfo"
      actionLabel="Re-Activate"
      :value="showReactivatePopup"
      :termination="false"
      :deletePopup="false"
      :applyBorderClass="true"
      icon="currency_icon"
      @onAction="confirmReactivate"
      @input="cancelReactivate"
    />
    <ChecklistDrawer
      :isCountryChatter="false"
      ref="drawerContainer"
      :drawer="drawerOpen"
      :listCriteria="currencyCriteria"
      @closeDrawer="closeChatter"
      :chatterList="chatterList"
      :totalCheckpointChatter="totalCheckpointChatter"
      @requestMoreCheckpointsChatter="$emit('requestMoreChatter', $event)"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import UniversalTable from "@components/UniversalTable/UniversalTable.vue";
import OperatingCurrenciesTableFilterModal from "@components/OperatingCurrenciesTableFilterModal/OperatingCurrenciesTableFilterModal.vue";
import AddOperatingCurrencyDialog from "@components/AddOperatingCurrencyDialog/AddOperatingCurrencyDialog.vue";
import ExActionsPopUp from "@components/common/ExActionsPopUp.vue";
import ChecklistDrawer from "@components/ChecklistCriteriaTable/ChecklistDrawer.vue";
import Button from "@components/buttons/Button.vue";
import { useTableConfig } from "./composables/useTableConfig";
import { useOperatingCurrenciesTableViewState } from "@components/OperatingCurrenciesTable/composables/useOperatingCurrenciesTableViewState";

const props = defineProps({
  currencies: {
    type: Array,
    default: () => [],
  },
  totalCount: {
    type: Number,
    default: 0,
  },
  externalTotal: {
    type: Number,
    default: null,
  },
  dropdownCurrencyList: {
    type: Array,
    default: () => [],
  },
  operatingCatalogCurrencyIds: {
    type: Array,
    default: () => [],
  },
  serverSidePagination: {
    type: Boolean,
    default: false,
  },
  syncedSearchQuery: {
    type: String,
    default: "",
  },
  chatterList: {
    type: Array,
    default: () => [],
  },
  totalCheckpointChatter: {
    type: Number,
    default: 0,
  },
  addErrorCode: {
    type: String,
    default: "",
  },
});

const emit = defineEmits([
  "fetch-currencies",
  "search-currencies",
  "sort-currencies",
  "filter-currencies",
  "add-currency",
  "add-currency-confirm",
  "onAddCurrency",
  "set-rate",
  "rate-mode-change",
  "menu-action",
  "chatter-click",
  "bulk-set-mode",
  "get-chatter-list",
  "requestMoreChatter",
  "currency-page-change",
  "currency-search",
  "currency-status-filter",
  "reloadData",
  "ensure-currency-catalog",
]);

const tableConfig = useTableConfig({
  onSetRate: (payload) => emit("set-rate", payload),
  onRateModeChange: (payload) => emit("rate-mode-change", payload),
  onMenuAction: (payload) => handleMenuAction(payload),
  onChatterClick: (payload) => openChatter(payload?.item),
});
const viewState = useOperatingCurrenciesTableViewState();

const showRetirePopup = ref(false);
const showReactivatePopup = ref(false);
const pendingMenuItem = ref(null);
const drawerOpen = ref(false);
const currencyCriteria = ref("");

const currentPage = ref(1);
const itemsPerPage = ref(10);
const showAddDialog = ref(false);

const RATE_MODE_LABELS = { daily: "Auto", corporate: "Corporate" };
const capitalize = (s) =>
  typeof s === "string" ? s.charAt(0).toUpperCase() + s.slice(1) : s;

const rowName = (item) => item.name || item.currency_name || "";
const rowCode = (item) => item.code || item.currency_code || "";
const rowStatus = (item) =>
  item.status || (item.active === false ? "retired" : "active");
const rowRateMode = (item) =>
  item.rate_mode === "corporate" ? "corporate" : "daily";
const rowIsReference = (item) =>
  item.is_reference === true || item.reference === true;

watch(
  () => props.syncedSearchQuery,
  (val) => {
    if (!props.serverSidePagination) return;
    const next = val == null ? "" : String(val);
    if (next !== (viewState.state.filters.searchText || "")) {
      viewState.state.filters.searchText = next;
    }
  }
);

const retireHtmlInfo = computed(() => {
  const name = rowName(pendingMenuItem.value) || "this currency";
  return `You are about to retire <strong>${name}</strong>. Retired currencies can be re-activated later.`;
});

const reactivateHtmlInfo = computed(() => {
  const name = rowName(pendingMenuItem.value) || "this currency";
  return `You are about to re-activate <strong>${name}</strong>.`;
});

// Applied-filter chips combine Status and Rate mode (Figma node 1555:52).
// A single selected rate mode is a real narrowing; both/none shows no chip.
const appliedChips = computed(() => {
  const chips = (viewState.state.filters.values || []).map(
    (s) => `Status: ${capitalize(s)}`
  );
  const rateModes = viewState.state.filters.rateModeValues || [];
  if (rateModes.length === 1) {
    chips.push(`Rate mode: ${RATE_MODE_LABELS[rateModes[0]] || capitalize(rateModes[0])}`);
  }
  return chips;
});

const tableConfigWithFilters = computed(() => {
  return {
    ...tableConfig.tableParams.value,
    filters: {
      values: appliedChips.value,
      chipLabels: appliedChips.value,
      searchText: viewState.state.filters.searchText,
      searchGhostText: viewState.state.filters.searchGhostText,
    },
  };
});

// Client-side view of the provided operating_currencies collection (AC):
// Status · Rate mode · Search by currency_name, then Reference first + A→Z name.
const filteredCurrencies = computed(() => {
  let list = [...(props.currencies || [])];

  if (!props.serverSidePagination) {
    const statuses = viewState.state.filters.values || [];
    if (statuses.length > 0) {
      list = list.filter((item) => statuses.includes(rowStatus(item)));
    }

    const search = (viewState.state.filters.searchText || "").trim().toLowerCase();
    if (search) {
      list = list.filter((item) => {
        const name = rowName(item).toLowerCase();
        const code = rowCode(item).toLowerCase();
        return name.includes(search) || code.includes(search);
      });
    }
  }

  // Both modes selected (or empty) = no rate-mode narrowing.
  const rateModes = viewState.state.filters.rateModeValues || [];
  if (rateModes.length === 1) {
    list = list.filter(
      (item) => rowIsReference(item) || rateModes.includes(rowRateMode(item))
    );
  }

  list.sort((a, b) => {
    const aRef = rowIsReference(a);
    const bRef = rowIsReference(b);
    if (aRef && !bRef) return -1;
    if (!aRef && bRef) return 1;
    return rowName(a).localeCompare(rowName(b), undefined, {
      sensitivity: "base",
    });
  });

  return list;
});

const filteredCount = computed(() => {
  if (props.serverSidePagination) {
    if (typeof props.externalTotal === "number") return props.externalTotal;
    if (typeof props.totalCount === "number" && props.totalCount > 0) {
      return props.totalCount;
    }
  }
  return filteredCurrencies.value.length;
});

const paginatedCurrencies = computed(() => {
  if (props.serverSidePagination) {
    return filteredCurrencies.value;
  }
  const start = (currentPage.value - 1) * itemsPerPage.value;
  return filteredCurrencies.value.slice(start, start + itemsPerPage.value);
});

const handleSearch = (search) => {
  viewState.state.filters.searchText = search || "";
  currentPage.value = 1;
  emit("search-currencies", { search });
  if (props.serverSidePagination) {
    emit("currency-search", search || "");
  }
};

const handleSort = (sortData) => {
  emit("sort-currencies", sortData);
};

const handleRequest = async ({ page, itemsPerPage: perPage }) => {
  currentPage.value = page;
  itemsPerPage.value = perPage || 10;
  emit("fetch-currencies", { page, itemsPerPage: perPage || 10 });
  if (props.serverSidePagination) {
    emit("currency-page-change", page);
  }
};

const emitStatusFilter = () => {
  if (props.serverSidePagination) {
    emit("currency-status-filter", [...viewState.state.filters.values]);
  }
};

// OperatingCurrenciesTableFilterModal emits apply(statusFilter, rateModeFilter).
const applyFilters = (statusFilter, rateModeFilter) => {
  viewState.state.filters.values = statusFilter || [];
  viewState.state.filters.rateModeValues =
    rateModeFilter && rateModeFilter.length
      ? rateModeFilter
      : ["daily", "corporate"];
  viewState.state.filters.showFilterDialog = false;
  currentPage.value = 1;

  emit("filter-currencies", {
    status: viewState.state.filters.values,
    rateMode: viewState.state.filters.rateModeValues,
    action: "apply",
  });
  emitStatusFilter();
};

// Chip removal from the toolbar: strip a Status value, or clear the rate-mode
// narrowing (back to both modes).
const removeFilter = async (chip) => {
  const label = typeof chip === "object" ? chip.value ?? chip.label : chip;
  if (typeof label === "string" && label.startsWith("Status: ")) {
    const status = label.replace("Status: ", "").toLowerCase();
    viewState.state.filters.values = viewState.state.filters.values.filter(
      (s) => s !== status
    );
  } else if (typeof label === "string" && label.startsWith("Rate mode: ")) {
    viewState.state.filters.rateModeValues = ["daily", "corporate"];
  }
  currentPage.value = 1;

  emit("filter-currencies", {
    status: viewState.state.filters.values,
    rateMode: viewState.state.filters.rateModeValues,
    action: "remove",
  });
  emitStatusFilter();
};

// Empty-state "Clear Filter" / "Clear All" reset to the documented default
// (Status: Active), not an unfiltered view — Confluence + QA on ER-10445.
const DEFAULT_STATUS_FILTER = ["active"];
const DEFAULT_RATE_MODE_FILTER = ["daily", "corporate"];

const removeAllFilters = async () => {
  viewState.state.filters.values = [...DEFAULT_STATUS_FILTER];
  viewState.state.filters.rateModeValues = [...DEFAULT_RATE_MODE_FILTER];
  viewState.state.filters.searchText = "";
  currentPage.value = 1;

  emit("filter-currencies", {
    status: viewState.state.filters.values,
    rateMode: viewState.state.filters.rateModeValues,
    action: "removeAll",
  });
  if (props.serverSidePagination) {
    emit("currency-search", "");
    emit("currency-status-filter", [...DEFAULT_STATUS_FILTER]);
  }
};

const handleAddCurrency = () => {
  showAddDialog.value = true;
  emit("add-currency");
  emit("ensure-currency-catalog");
};

const onAddCurrencyConfirm = (currency) => {
  const name =
    (typeof currency?.name === "string" && currency.name.trim()) ||
    (typeof currency?.currency_name === "string" && currency.currency_name.trim()) ||
    "";
  if (name) {
    viewState.state.filters.searchText = name;
    currentPage.value = 1;
    emit("currency-search", name);
  }
  emit("add-currency-confirm", currency, name);
  emit("onAddCurrency", currency, name);
  showAddDialog.value = false;
};

function handleMenuAction(payload) {
  const action = payload?.action;
  const item = payload?.item;
  if (!action || !item) return;

  if (action === "retire") {
    pendingMenuItem.value = item;
    showRetirePopup.value = true;
    return;
  }
  if (action === "re-activate" || action === "reactivate") {
    pendingMenuItem.value = item;
    showReactivatePopup.value = true;
    return;
  }

  emit("menu-action", { item, action });
}

function confirmRetire() {
  if (pendingMenuItem.value) {
    emit("menu-action", { item: pendingMenuItem.value, action: "retire" });
  }
  showRetirePopup.value = false;
  pendingMenuItem.value = null;
}

function cancelRetire() {
  showRetirePopup.value = false;
  pendingMenuItem.value = null;
}

function confirmReactivate() {
  if (pendingMenuItem.value) {
    emit("menu-action", {
      item: pendingMenuItem.value,
      action: "re-activate",
    });
  }
  showReactivatePopup.value = false;
  pendingMenuItem.value = null;
}

function cancelReactivate() {
  showReactivatePopup.value = false;
  pendingMenuItem.value = null;
}

function openChatter(item) {
  if (!item) return;
  drawerOpen.value = true;
  currencyCriteria.value = rowName(item);
  emit("chatter-click", { item });
  emit("get-chatter-list", item);
}

function closeChatter() {
  drawerOpen.value = false;
  currencyCriteria.value = "";
}

defineExpose({
  handleMenuAction,
  confirmRetire,
  showRetirePopup,
  filteredCurrencies,
  paginatedCurrencies,
});

// bulk-set-mode applies to every non-reference row in the account in one call;
// reference and already-in-mode rows are skipped by the backend.
const handleBulkSetMode = (mode) => {
  emit("bulk-set-mode", { mode });
};
</script>

<style scoped>
.operating-currencies-table {
  width: 100%;
}

.operating-currencies-table__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.operating-currencies-table__title {
  color: #101828;
  font-family: Inter;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  margin: 0 0 4px;
}

.operating-currencies-table__subtitle {
  color: #667085;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  margin: 0;
}

.operating-currencies-table__bulk-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.operating-currencies-table__fx-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 16px;
  padding: 12px 20px;
  background: #eff6ff;
  border-top: 1px solid #bbddfe;
  border-bottom: 1px solid #bbddfe;
}

.operating-currencies-table__fx-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  border-radius: 99px;
  background: #3b82f6;
  color: #ffffff;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
}

.operating-currencies-table__fx-text {
  margin: 0;
  min-width: 0;
  flex: 1;
  color: #475668;
  font-size: 13px;
  line-height: 20px;
  overflow-wrap: anywhere;
}

.operating-currencies-table__fx-text strong {
  color: #101828;
  font-weight: 600;
}

.operating-currencies-table__fx-link {
  color: #1d66e2;
  font-weight: 500;
  text-decoration: underline;
}
</style>

<!--
  Column alignment for the 7-column layout (Currency · Symbol · Exchange rate ·
  Status · Rate mode · Chatter · Menu). Global (unscoped) because UniversalTable
  renders the table rows; namespaced under .operating-currencies-table so no
  other table is affected.
-->
<style>
.operating-currencies-table .ex-universal-table-container {
  box-shadow: 0px 1px 2px 0px #1018280f, 0px 1px 3px 0px #1018281a !important;
  border-radius: 8px;
}

.operating-currencies-table tbody tr td {
  padding-top: 16px !important;
  padding-bottom: 16px !important;
}

/* Center Symbol (2), Status (4), Chatter (6), Menu (7). */
.operating-currencies-table thead tr th:nth-child(2),
.operating-currencies-table thead tr th:nth-child(4),
.operating-currencies-table thead tr th:nth-child(6),
.operating-currencies-table thead tr th:nth-child(7),
.operating-currencies-table tbody tr td:nth-child(2),
.operating-currencies-table tbody tr td:nth-child(4),
.operating-currencies-table tbody tr td:nth-child(6),
.operating-currencies-table tbody tr td:nth-child(7) {
  text-align: center !important;
}

.operating-currencies-table thead tr th:nth-child(2) > div,
.operating-currencies-table thead tr th:nth-child(4) > div,
.operating-currencies-table thead tr th:nth-child(6) > div,
.operating-currencies-table thead tr th:nth-child(7) > div {
  justify-content: center !important;
}

/* Currency column uses the full column width so name + Reference badge stay on one line. */
.operating-currencies-table tbody tr td:nth-child(1) .cell-container {
  max-width: none;
}

.operating-currencies-table tbody tr td:nth-child(2) .cell-container,
.operating-currencies-table tbody tr td:nth-child(4) .cell-container,
.operating-currencies-table tbody tr td:nth-child(6) .cell-container,
.operating-currencies-table tbody tr td:nth-child(7) .cell-container {
  margin-left: auto;
  margin-right: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: none !important;
  overflow: visible;
}

.operating-currencies-table tbody tr td:nth-child(7) {
  overflow: visible;
  padding-right: 8px !important;
}

.operating-currencies-table .ex-menu-icon {
  margin: 0 !important;
}
</style>
